import { MonitorSmartphone, NotebookPen, Paintbrush2, Swords, Plane, MonitorPlay } from 'lucide-react';

export const MESSENGER_ACTION_PREFIX = '::mapleos-action::';

export type MessengerInviteCategory = 'invite' | 'activity' | 'game';

export interface MessengerAppInvitePayload {
  version: 1;
  type: 'app-invite';
  category: MessengerInviteCategory;
  appId: string;
  title: string;
  description: string;
  note?: string;
}

export interface MessengerInviteOption {
  appId: string;
  title: string;
  description: string;
  category: Exclude<MessengerInviteCategory, 'invite'>;
  icon: typeof NotebookPen;
}

export const MESSENGER_ACTIVITY_OPTIONS: MessengerInviteOption[] = [
  {
    appId: 'notepad',
    title: 'Notepad',
    description: 'Swap notes and sketch ideas together in MapleOS.',
    category: 'activity',
    icon: NotebookPen,
  },
  {
    appId: 'paint-program',
    title: 'Paint',
    description: 'Jump into Paint for quick mockups and doodles.',
    category: 'activity',
    icon: Paintbrush2,
  },
  {
    appId: 'ai-chat',
    title: 'AI Chat',
    description: 'Open AI Chat together and compare prompts.',
    category: 'activity',
    icon: MonitorSmartphone,
  },
];

export const MESSENGER_GAME_OPTIONS: MessengerInviteOption[] = [
  {
    appId: 'cortex',
    title: 'Cortex',
    description: 'Invite them into a quick futuristic arena run.',
    category: 'game',
    icon: Swords,
  },
  {
    appId: 'flight-sim',
    title: 'FlightSim',
    description: 'Launch FlightSim and jump into the cockpit.',
    category: 'game',
    icon: Plane,
  },
  {
    appId: 'DOSEmulator',
    title: 'DOS Emulator',
    description: 'Relive retro classics in the DOS Emulator.',
    category: 'game',
    icon: MonitorPlay,
  },
];

export const getInviteOptions = (mode: MessengerInviteCategory) => {
  if (mode === 'activity') {
    return MESSENGER_ACTIVITY_OPTIONS;
  }

  if (mode === 'game') {
    return MESSENGER_GAME_OPTIONS;
  }

  return [...MESSENGER_ACTIVITY_OPTIONS, ...MESSENGER_GAME_OPTIONS];
};

export const serializeMessengerAction = (payload: MessengerAppInvitePayload) =>
  `${MESSENGER_ACTION_PREFIX}${JSON.stringify(payload)}`;

export const parseMessengerAction = (content: string): MessengerAppInvitePayload | null => {
  if (!content.startsWith(MESSENGER_ACTION_PREFIX)) {
    return null;
  }

  try {
    const parsed = JSON.parse(content.slice(MESSENGER_ACTION_PREFIX.length)) as MessengerAppInvitePayload;
    if (parsed?.type !== 'app-invite' || !parsed?.appId || !parsed?.title || !parsed?.description) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

export const getMessengerActionPreview = (content: string) => {
  const payload = parseMessengerAction(content);
  if (!payload) {
    return null;
  }

  const categoryLabel = payload.category === 'game' ? 'game' : 'activity';
  return `Invitation to ${payload.title} (${categoryLabel})`;
};
